import { useApolloClient } from "@apollo/client";
import { ModalOpenButton } from "@msys/ui";
import { GroupAdd as GroupAddIcon } from "@mui/icons-material";
import { Button, ButtonProps } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  QuoteTemplateShareButton_QuoteTemplateFragment,
  useShareQuoteTemplatePubliclyMutation,
  useShareQuoteTemplateWithOrganisationsMutation,
} from "./QuoteTemplateShareButton.generated.js";
import { ConfirmModal } from "../../../../commons/modals/ConfirmModal.js";

interface Props extends ButtonProps {
  doc: QuoteTemplateShareButton_QuoteTemplateFragment;
  buttonProps?: ButtonProps;
  buttonTitle?: string;
  versionNumber: number;
  refetchQueries?: string[];
}

export const QuoteTemplateUpdateShareButton = ({
  versionNumber,
  doc,
  buttonProps,
  buttonTitle,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["TemplatesSharing", "Global"]);
  const handleUpdateShare = useQuoteTemplateUpdateShare({
    refetchQueries,
  });
  return canUpdateShare(doc, versionNumber) ? (
    <ModalOpenButton
      Modal={ConfirmModal}
      modalProps={{
        title: t(
          "Are you sure you want to update sharing to Meister Store to this version?",
          { ns: "TemplatesSharing" }
        ),
        handleConfirm: async () => {
          await handleUpdateShare(doc, versionNumber);
        },
      }}
    >
      <Button
        startIcon={<GroupAddIcon />}
        variant="contained"
        color="secondary"
        {...buttonProps}
      >
        {buttonTitle ?? t("Share", { ns: "TemplatesSharing" })}
      </Button>
    </ModalOpenButton>
  ) : null;
};

export function canUpdateShare(
  doc: QuoteTemplateShareButton_QuoteTemplateFragment,
  versionNumber: number
) {
  const templateSharing = doc.templateSharing;
  return (
    doc.hasAnyPublishedVersion &&
    doc.latestPublishedVersionNumber &&
    versionNumber >= 1 &&
    versionNumber <= doc.latestPublishedVersionNumber &&
    templateSharing &&
    templateSharing.latestSharedVersionNumber < versionNumber
  );
}

export function useQuoteTemplateUpdateShare({
  refetchQueries,
}: {
  refetchQueries?: string[];
}) {
  const client = useApolloClient();

  const [shareWithOrganisations] =
    useShareQuoteTemplateWithOrganisationsMutation({
      client,
      refetchQueries,
      awaitRefetchQueries: true,
    });
  const [sharePublicly] = useShareQuoteTemplatePubliclyMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const handleUpdateShare = async (
    doc: QuoteTemplateShareButton_QuoteTemplateFragment,
    versionNumber: number
  ) => {
    const templateSharing = doc.templateSharing;
    if (templateSharing?.sharedPublicly) {
      await sharePublicly({
        variables: {
          input: {
            quoteTemplateId: doc.id,
            versionNumber,
          },
        },
      });
    } else if (
      doc.templateSharing &&
      doc.templateSharing.sharedWithOrganisations.length > 0
    ) {
      await shareWithOrganisations({
        variables: {
          input: {
            quoteTemplateId: doc.id,
            sharedWithSystemOrganisationIds:
              doc.templateSharing.sharedWithOrganisations.map(o => o.id),
            versionNumber,
          },
        },
      });
    }
  };

  return handleUpdateShare;
}
